import { ChangeDetectionStrategy, Component, Input } from "@angular/core";
import { TypeManagerDecorator } from "../../../main/type.map.service";
import { S25IconComponent } from "../../s25-icon/s25.icon.component";

@TypeManagerDecorator("se-contact-tile")
@Component({
    selector: "se-contact-tile",
    template: `
        <div class="contact-header">
            @if (header) {
                <h3 class="contact-tile-header">{{ header }}</h3>
            }
            @if (showPrimaryBadge) {
                <div class="primary-badge">Primary</div>
            }
        </div>

        <div class="contact-content">
            @if (imageUrl) {
                <div class="contact-image-container">
                    <img [src]="imageUrl" [alt]="name + ' profile image'" class="contact-image" />
                </div>
            } @else {
                <div class="contact-image-container">
                    <div class="contact-image-placeholder">
                        <s25-ng-icon [type]="'contactFilled'" [size]="36"></s25-ng-icon>
                    </div>
                </div>
            }

            <div class="contact-details">
                <h4 class="contact-name">{{ name }}</h4>
                @if (title) {
                    <p class="contact-title">{{ title }}</p>
                }
                @if (email) {
                    <p class="contact-email">
                        <s25-ng-icon [type]="'mailFilled'" [size]="18"></s25-ng-icon>
                        <a [href]="'mailto:' + email">{{ email }}</a>
                    </p>
                }
                @if (phone) {
                    <p class="contact-phone">
                        <s25-ng-icon [type]="'phone'" [size]="18"></s25-ng-icon>
                        <a [href]="'tel:' + phone">{{ phone }}</a>
                    </p>
                }
            </div>
        </div>
    `,
    styles: `
        :host {
            background: white;
            border-radius: 16px;
            padding: 0;
            color: #333;
            box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
            border: 1px solid #e8e8e8;
            transition: all 0.3s ease;
            overflow: hidden;
            position: relative;
            display: block;
        }

        :host:hover {
            transform: translateY(-2px);
            box-shadow: 0 8px 30px rgba(0, 0, 0, 0.12);
        }

        .contact-header {
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            color: white;
            padding: 1.25rem 1.5rem;
            display: flex;
            justify-content: space-between;
            align-items: center;
            position: relative;
        }

        .contact-header::after {
            content: "";
            position: absolute;
            bottom: 0;
            left: 0;
            right: 0;
            height: 1px;
            background: rgba(255, 255, 255, 0.2);
        }

        .contact-tile-header {
            margin: 0;
            font-size: 1.1rem;
            font-weight: 600;
            color: white;
            letter-spacing: 0.5px;
        }

        .primary-badge {
            background: rgba(255, 255, 255, 0.2);
            color: white;
            padding: 0.4rem 0.8rem;
            border-radius: 20px;
            font-size: 0.75rem;
            font-weight: 600;
            backdrop-filter: blur(10px);
            border: 1px solid rgba(255, 255, 255, 0.3);
            text-transform: uppercase;
            letter-spacing: 0.5px;
        }

        .contact-content {
            padding: 1.5rem;
            display: flex;
            gap: 1.25rem;
            align-items: flex-start;
        }

        .contact-image-container {
            flex-shrink: 0;
            position: relative;
        }

        .contact-image {
            width: 80px;
            height: 80px;
            border-radius: 50%;
            object-fit: cover;
            border: 3px solid #f0f0f0;
            box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
        }

        .contact-image-placeholder {
            width: 80px;
            height: 80px;
            border-radius: 50%;
            background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
            display: flex;
            align-items: center;
            justify-content: center;
            border: 3px solid #f0f0f0;
            box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
        }

        ::ng-deep .contact-image-placeholder svg {
            opacity: 0.6;
            color: #6c757d;
        }

        .contact-details {
            flex: 1;
            min-width: 0;
        }

        .contact-name {
            margin: 0 0 0.5rem 0;
            font-size: 1.3rem;
            font-weight: 600;
            color: #2c3e50;
            line-height: 1.2;
        }

        .contact-title {
            margin: 0 0 1.25rem 0;
            color: #7f8c8d;
            font-style: italic;
            font-size: 0.95rem;
            font-weight: 400;
        }

        .contact-email,
        .contact-phone {
            margin: 0.75rem 0;
            display: flex;
            align-items: center;
            gap: 0.75rem;
            color: #555;
            padding: 0.5rem 0;
        }

        .contact-email a,
        .contact-phone a {
            color: #3498db;
            text-decoration: none;
            font-weight: 500;
            transition: color 0.2s ease;
        }

        .contact-email a:hover,
        .contact-phone a:hover {
            color: #2980b9;
            text-decoration: underline;
        }

        ::ng-deep .contact-email svg,
        ::ng-deep .contact-phone svg {
            opacity: 0.7;
            color: #95a5a6;
        }

        @media (max-width: 768px) {
            .contact-content {
                flex-direction: column;
                text-align: center;
            }

            .contact-image-container {
                align-self: center;
            }

            .contact-email,
            .contact-phone {
                justify-content: center;
            }
        }
    `,
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [S25IconComponent],
})
export class SeContactTileComponent {
    @Input() name: string;
    @Input() email?: string;
    @Input() phone?: string;
    @Input() title?: string;
    @Input() imageUrl?: string;
    @Input() showPrimaryBadge: boolean = false;
    @Input() header?: string;
}
